import React, { useMemo, useRef } from "react";
import Chart from "react-apexcharts";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { Tooltip } from "@material-tailwind/react";
import InformationIcon from "../../assets/icons/info/information.png";
import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  formatCount,
  type IReportBucket,
  type IReportSeries,
} from "../../pages/ParcelReport/parcelReportData";

/** Past a month of bars the white gaps and in-bar numbers crowd the bars out. */
const DENSE_BUCKETS = 31;

const DARK_INK = "#1f2937";

const luminance = (hex: string) => {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** White or dark numbers, whichever reads better on the segment's colour. */
const labelInk = (fill: string) => {
  const fillLuminance = luminance(fill);
  const whiteContrast = 1.05 / (fillLuminance + 0.05);
  const darkContrast = (fillLuminance + 0.05) / (luminance(DARK_INK) + 0.05);
  return whiteContrast >= darkContrast ? "#ffffff" : DARK_INK;
};

const csvCell = (value: string | number) => {
  let text = String(value ?? "");
  // A store name starting with = + - or @ would run as a formula in Excel.
  if (typeof value === "string" && /^[=+\-@]/.test(text)) text = `'${text}`;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

interface ParcelReportChartProps {
  title: string;
  info: string;
  buckets: IReportBucket[];
  /** The x-axis title and the CSV's first column. */
  bucketHeading: string;
  yAxisTitle: string;
  series: IReportSeries[];
  /** No report yet. */
  isLoading: boolean;
  emptyText: string;
  /** Shared by the CSV and the image, without the extension. */
  fileName: string;
  /** Shown before the download buttons. */
  controls?: React.ReactNode;
}

/** A stacked bar per day, week or month, for the Parcel report page. */
export const ParcelReportChart = React.memo(
  ({
    title,
    info,
    buckets,
    bucketHeading,
    yAxisTitle,
    series,
    isLoading,
    emptyText,
    fileName,
    controls,
  }: ParcelReportChartProps) => {
    const snapshotRef = useRef(null);
    const [, takeScreenshot] = useScreenshot();

    const hasData = useMemo(
      () => series.some((s) => s.data.some((value) => value > 0)),
      [series]
    );

    const chartSeries = useMemo(
      () => series.map(({ name, data }) => ({ name, data })),
      [series]
    );

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download = `${fileName}.png`;
        link.click();
      });
    };

    // The chart already holds every bucket, so the CSV is written from it
    // rather than fetched again. Weeks and months carry the days they cover,
    // so a partial first or last one is not read as a full period.
    const downloadCsv = () => {
      const byDay = buckets.every((bucket) => bucket.first === bucket.last);
      const lines = [
        [...(byDay ? [bucketHeading] : ["From", "To"]), ...series.map((s) => s.name)],
        ...buckets.map((bucket, index) => [
          ...(byDay ? [bucket.first] : [bucket.first, bucket.last]),
          ...series.map((s) => s.data[index] ?? 0),
        ]),
      ].map((cells) => cells.map(csvCell).join(","));
      const blob = new Blob(["\uFEFF", lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    };

    const chartOptions = useMemo(() => {
      const dense = buckets.length > DENSE_BUCKETS;
      // The tallest stacked bar. On a small scale (a handful of parcels or
      // people) the axis gets one tick per whole number, so it neither shows
      // empty fractional gridlines nor shrinks so far that its title is cut.
      const tallest = Math.max(
        0,
        ...buckets.map((_, index) =>
          series.reduce((sum, s) => sum + (s.data[index] ?? 0), 0)
        )
      );
      const smallScale = tallest > 0 && tallest <= 5;
      return {
        chart: {
          type: "bar" as const,
          stacked: true,
          zoom: { enabled: false },
          toolbar: { show: false },
        },
        stroke: {
          show: true,
          width: dense ? 0 : 2,
          colors: ["#fff"],
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: dense ? "80%" : "55%",
            borderRadius: 4,
            dataLabels: { position: "center" as const },
          },
        },
        dataLabels: {
          enabled: !dense,
          formatter: (val: number) => (val === 0 ? "" : formatCount(val)),
          style: {
            fontSize: "11px",
            fontWeight: "bold",
            colors: series.map((s) => labelInk(s.color)),
          },
        },
        xaxis: {
          categories: buckets.map((bucket) => bucket.label),
          title: {
            text: bucketHeading,
            style: { fontSize: "14px", fontWeight: "bold" },
          },
          labels: {
            style: { fontSize: "12px" },
            maxWidth: 120,
          },
        },
        yaxis: {
          min: 0,
          ...(smallScale ? { max: tallest, tickAmount: tallest } : {}),
          title: {
            text: yAxisTitle,
            style: { fontSize: "14px", fontWeight: "bold" },
          },
          labels: {
            minWidth: 24,
            // Counts are whole numbers; a fractional tick on a small scale
            // would otherwise round to a repeat of its neighbour.
            formatter: (val: number) =>
              Math.abs(val - Math.round(val)) < 1e-6
                ? formatCount(Math.round(val))
                : "",
          },
        },
        grid: { strokeDashArray: 5 },
        legend: {
          show: true,
          position: "right" as const,
          fontSize: "14px",
          fontFamily: "Satoshi, sans-serif",
          labels: {
            colors: "#374151",
          },
          markers: {
            width: 12,
            height: 12,
            strokeWidth: 0,
            radius: 6,
          },
          itemMargin: {
            horizontal: 0,
            vertical: 4,
          },
        },
        colors: series.map((s) => s.color),
        // Long store names in a right-hand legend squeeze the bars on narrow
        // screens; below a desktop width the legend goes under the chart,
        // where its entries sit side by side and need room between them.
        responsive: [
          {
            breakpoint: 1024,
            options: {
              legend: {
                position: "bottom" as const,
                itemMargin: { horizontal: 8 },
              },
            },
          },
          // On a phone a week of "10 Jun" labels just fits without rotating
          // and runs together; a smaller size leaves a gap between them.
          {
            breakpoint: 640,
            options: { xaxis: { labels: { style: { fontSize: "10px" } } } },
          },
        ],
        tooltip: {
          shared: true,
          intersect: false,
          x: {
            // The days the bucket covers, so a partial first or last week
            // reads as one rather than as a dip.
            formatter: (val: number, opts?: any) =>
              buckets[opts?.dataPointIndex]?.title ?? String(val),
          },
          y: {
            formatter: (val: number) => formatCount(val),
          },
        },
      };
    }, [buckets, bucketHeading, yAxisTitle, series]);

    return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row sm:flex-wrap w-full items-start sm:items-center justify-end gap-2 sm:gap-3">
          {controls}
          {hasData && !isLoading && (
            <>
              <button
                type="button"
                onClick={downloadCsv}
                className="bg-droppie-oranje border border-gray-border h-8 px-3 sm:px-6 py-0 rounded active:bg-droppie-zalm disabled:bg-sky-light transition ease-linear flex items-center justify-center gap-2"
              >
                <p className="font-satoshi font-medium text-white text-md sm:text-md">
                  Download CSV
                </p>
              </button>
              <PrimaryButton
                size="sm"
                onClick={downloadScreenshot}
                label="Download image"
                className="!bg-droppie-oranje !border-droppie-oranje"
              />
            </>
          )}
        </div>

        <div ref={snapshotRef} className="mt-4">
          <div className="flex items-center mb-2 sm:mb-0">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
              {title}
            </p>
            <Tooltip content={info} placeholder={undefined}>
              <img
                src={InformationIcon}
                alt="info"
                className="inline-block w-4 h-4 ml-2 cursor-pointer"
              />
            </Tooltip>
          </div>

          <div className="mt-4 h-[450px]">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-[450px] w-full gap-2">
                <svg
                  aria-hidden="true"
                  className={`w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary-base`}
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              </div>
            ) : hasData ? (
              <Chart
                // react-apexcharts compares options as JSON, which drops the
                // tooltip formatter; a new range with the same axis labels
                // would keep the old bucket dates. Remount when they change.
                key={`${buckets.length}|${buckets[0]?.title}|${
                  buckets[buckets.length - 1]?.title
                }`}
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={450}
              />
            ) : (
              <div className="flex items-center justify-center h-[300px] w-full">
                <p className="text-gray-500 font-satoshi">{emptyText}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);
